import { Location } from '@angular/common';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { ActivationStart, Router } from '@angular/router';
import { BehaviorSubject, fromEvent, interval, Observable, of, Subscription } from 'rxjs';
import { filter, first, map, switchMap, take, throttleTime } from 'rxjs/operators';
import { AUTH_MAIN_ACCOUNT_ID, AUTH_PRODUCT_ID, AUTH_SERVER_URL, AUTH_USE_PRODUCT_LOGIN } from './auth.model';
import { SessionInactivityService } from './session-inactivity.service';

const ACCESS_TOKEN_STORAGE_KEY = 'access-token';
const ACCESS_TOKEN_EXPIRY_STORAGE_KEY = 'access-token-expiry';
const REFRESH_TOKEN_STORAGE_KEY = 'refresh-token';
const REFRESH_TOKEN_EXPIRY_STORAGE_KEY = 'refresh-token-expiry';
const USER_ID_STORAGE_KEY = 'user-id';
const LAST_LOGGED_IN_STORAGE_KEY = 'last-logged-in';
const REDIRECT_URL_STORAGE_KEY = 'redirect-url';
const REFRESH_CHECK_INTERVAL_MS = 15000;
const REFRESH_BEFORE_EXPIRY_MS = 120000;

interface AuthTokenResponse {
    token: string;
    tokenExpiry: string;
    refreshToken: string;
    refreshTokenExpiry: string;
    lastLoggedIn?: string;
}

export class GatewayIdentityUser {
    constructor(
        public userId: number,
        public email: string,
        public fullName: string,
        public roles: string[],
        public claims: string[],
        public token: string,
        public accountId: number,
        public accountName: string
    ) { }
}

export class Credentials {
    constructor(public userId: string, public token: string) { }
}

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
    public readonly gatewayIdentityUserObservable: Observable<GatewayIdentityUser>;

    private readonly userSubject: BehaviorSubject<GatewayIdentityUser>;
    private readonly tokenSubject: BehaviorSubject<Credentials>;
    private readonly subscriptions = new Subscription();
    private refreshRequest: Promise<void> | null = null;
    private logoutInProgress = false;

    constructor(
        private readonly router: Router,
        private readonly httpClient: HttpClient,
        private readonly inactivity: SessionInactivityService,
        public readonly location: Location,
        @Inject(AUTH_SERVER_URL) public readonly _authServerUrl: string,
        @Inject(AUTH_PRODUCT_ID) private readonly productCode: string,
        @Inject(AUTH_MAIN_ACCOUNT_ID) private readonly authMainAccountId: number,
        @Inject(AUTH_USE_PRODUCT_LOGIN) private readonly authUseProductLogin: boolean
    ) {
        const isLoginCallback = this.hasLoginToken();
        const accessToken = isLoginCallback ? null : localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
        const userId = isLoginCallback ? null : localStorage.getItem(USER_ID_STORAGE_KEY);

        this.userSubject = new BehaviorSubject(null);
        this.tokenSubject = new BehaviorSubject(userId && accessToken ? new Credentials(userId, accessToken) : null);
        this.gatewayIdentityUserObservable = this.userSubject.asObservable();

        this.inactivity.start(
            () => !this.logoutInProgress && !this.hasLoginToken() && !!localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY),
            () => this.logoutAfterInactivity()
        );
        this.monitorRemoteLogout();
        this.initializeSession();
    }

    private initializeSession(): void {
        this.listenForLoginCallback();
        if (this.inactivity.check()) {
            return;
        }

        this.refreshAccessTokenIfNeeded()
            .catch(() => undefined)
            .then(() => {
                if (!this.logoutInProgress) {
                    this.loadIdentityWhenAuthenticated();
                }
            });

        this.subscriptions.add(interval(REFRESH_CHECK_INTERVAL_MS).subscribe(() => {
            if (!this.logoutInProgress && !this.inactivity.check()) {
                this.refreshAccessTokenIfNeeded().catch(() => undefined);
            }
        }));
    }

    private listenForLoginCallback(): void {
        this.subscriptions.add(this.router.events.pipe(
            filter(event => event instanceof ActivationStart && !!event.snapshot.queryParams.token),
            map((event: ActivationStart) => event.snapshot.queryParams),
            throttleTime(100)
        ).subscribe(async params => {
            if (params.key) {
                localStorage.setItem('key', params.key);
            }

            await this.exchangeLoginToken(params.token, params.userId);
            this.router.navigate([], {
                queryParams: { token: null, key: null, userId: null },
                queryParamsHandling: 'merge'
            });
        }));
    }

    private async exchangeLoginToken(token: string, userId: string): Promise<void> {
        const result = await this.sendLoginRequest(token, userId);
        this.storeAuthResult(result, userId);
        this.inactivity.reset();
    }

    private sendLoginRequest(token: string, userId: string): Promise<HttpResponse<AuthTokenResponse>> {
        return this.httpClient.post<AuthTokenResponse>(
            `${this._authServerUrl}api/${this.productCode}/login`,
            { userId, token, refreshTokenMode: 'body' },
            {
                headers: { 'Authorization': 'Bearer ' + token },
                observe: 'response',
                withCredentials: false
            }
        ).pipe(first()).toPromise();
    }

    private refreshAccessTokenIfNeeded(): Promise<void> {
        if (this.logoutInProgress || this.hasLoginToken()) {
            return Promise.resolve();
        }

        const accessToken = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
        const userId = localStorage.getItem(USER_ID_STORAGE_KEY);

        if (!accessToken || !userId) {
            this.clearRefreshMetadata();
            return Promise.resolve();
        }

        if (!refreshToken) {
            if (!this.refreshRequest) {
                return this.startRefresh(this.sendLoginRequest(accessToken, userId), userId);
            }
            return this.refreshRequest;
        }

        if (this.tokenSubject.getValue()?.token !== accessToken) {
            this.tokenSubject.next(new Credentials(userId, accessToken));
        }

        if (!this.isAccessTokenExpiring()) {
            return Promise.resolve();
        }

        if (!this.refreshRequest) {
            return this.startRefresh(this.sendRefreshRequest(refreshToken, accessToken), userId);
        }
        return this.refreshRequest;
    }

    private startRefresh(request: Promise<HttpResponse<AuthTokenResponse>>, userId: string): Promise<void> {
        this.refreshRequest = request
            .then(result => this.storeAuthResult(result, userId))
            .finally(() => this.refreshRequest = null);
        return this.refreshRequest;
    }

    private sendRefreshRequest(refreshToken: string, accessToken: string): Promise<HttpResponse<AuthTokenResponse>> {
        return this.httpClient.post<AuthTokenResponse>(
            `${this._authServerUrl}api/${this.productCode}/refresh`,
            { refreshToken, refreshTokenMode: 'body' },
            {
                headers: { 'Authorization': 'Bearer ' + accessToken },
                observe: 'response',
                withCredentials: false
            }
        ).pipe(first()).toPromise();
    }

    private storeAuthResult(result: HttpResponse<AuthTokenResponse>, userId: string): void {
        const response = result.body;
        const accessToken = response?.token || result.headers.get('Authorization');

        if (!accessToken || !response?.tokenExpiry || !response?.refreshToken || !response?.refreshTokenExpiry) {
            throw new Error('Auth response did not include the required session tokens.');
        }

        localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
        localStorage.setItem(ACCESS_TOKEN_EXPIRY_STORAGE_KEY, response.tokenExpiry);
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, response.refreshToken);
        localStorage.setItem(REFRESH_TOKEN_EXPIRY_STORAGE_KEY, response.refreshTokenExpiry);
        localStorage.setItem(USER_ID_STORAGE_KEY, userId);
        if (response.lastLoggedIn) {
            localStorage.setItem(LAST_LOGGED_IN_STORAGE_KEY, response.lastLoggedIn);
        }

        this.tokenSubject.next(new Credentials(userId, accessToken));
    }

    private isAccessTokenExpiring(): boolean {
        const expiry = Date.parse(localStorage.getItem(ACCESS_TOKEN_EXPIRY_STORAGE_KEY) || '');
        return Number.isNaN(expiry) || expiry <= Date.now() + REFRESH_BEFORE_EXPIRY_MS;
    }

    private loadIdentityWhenAuthenticated(): void {
        this.subscriptions.add(this.tokenSubject.pipe(
            filter(credentials => credentials != null),
            switchMap(() => this.httpClient.get<any>('/api/es/v1/user')),
            map(result => new GatewayIdentityUser(
                result.userId,
                result.email,
                result.fullName,
                result.roles,
                result.claims,
                result.token,
                result.accountId,
                result.accountName
            ))
        ).subscribe(this.userSubject));
    }

    private monitorRemoteLogout(): void {
        this.subscriptions.add(fromEvent<StorageEvent>(window, 'storage').subscribe(event => {
            const accessTokenWasRemoved = event.key === ACCESS_TOKEN_STORAGE_KEY && event.oldValue && !event.newValue;
            if (accessTokenWasRemoved && this.tokenSubject.getValue() && !this.logoutInProgress) {
                this.goToLogin(this.location.path() || '/');
            }
        }));
    }

    private hasLoginToken(): boolean {
        return !!new URLSearchParams(window.location.search).get('token');
    }

    public check(): Observable<boolean> {
        if (this.userSubject.getValue()) {
            return of(true);
        }

        if (this.tokenSubject.getValue()) {
            return this.userSubject.pipe(
                filter(user => user != null),
                take(1),
                map(() => true)
            );
        }

        return of(false);
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.userSubject.complete();
        this.tokenSubject.complete();
    }

    public goToLogin(redirectURL: string): void {
        this.logoutInProgress = true;
        this.inactivity.dismissWarning();
        localStorage.setItem(REDIRECT_URL_STORAGE_KEY, redirectURL);
        localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
        this.clearRefreshMetadata();
        this.inactivity.clear();
        localStorage.removeItem(USER_ID_STORAGE_KEY);

        const landingUrl = new URL(window.location.origin + '/auth/landing');
        const landingUrlEncoded = encodeURIComponent(landingUrl.toString());
        const authTokenGenerator = encodeURIComponent(`/api/${this.productCode}/redirect?url=${landingUrlEncoded}`);
        const queryString = this.authUseProductLogin ? this.getProductLoginUrl(authTokenGenerator) : `?redirect=${authTokenGenerator}`;

        window.location.href = this._authServerUrl.endsWith('public/')
            ? '/'
            : `${this._authServerUrl}/auth/login${queryString}`;
    }

    private clearRefreshMetadata(): void {
        localStorage.removeItem(ACCESS_TOKEN_EXPIRY_STORAGE_KEY);
        localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
        localStorage.removeItem(REFRESH_TOKEN_EXPIRY_STORAGE_KEY);
    }

    private async logoutAfterInactivity(): Promise<void> {
        if (this.logoutInProgress) {
            return;
        }

        this.logoutInProgress = true;
        this.inactivity.dismissWarning();
        try {
            if (this.refreshRequest) {
                await this.refreshRequest.catch(() => undefined);
            }

            const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
            const accessToken = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
            if (refreshToken) {
                await this.httpClient.post(
                    `${this._authServerUrl}api/${this.productCode}/logout?noRedirect=true`,
                    { refreshToken },
                    {
                        headers: { 'Authorization': 'Bearer ' + (accessToken || '') },
                        withCredentials: false
                    }
                ).pipe(first()).toPromise();
            }
        } catch {
            // Local logout must continue if server-side revocation is unavailable.
        } finally {
            this.goToLogin(this.location.path() || '/');
        }
    }

    private getProductLoginUrl(redirect: string): string {
        let productLoginUrl = `?productKey=${this.productCode}`;
        if (this.authMainAccountId > 0) {
            productLoginUrl += `&accountId=${this.authMainAccountId}`;
        }
        return productLoginUrl + `&redirect=${redirect}`;
    }
}
